import * as React from 'react';
import Link from 'next/link';

import MenuIcon from '/public/assets/themes/canvas/img/menu-icon.svg';
import cx from 'classnames';
import { CanvasAnimatedTile } from 'themes/canvas/components/CanvasAnimatedTile';

import { Heading } from 'components/Heading';
import CloseIcon from 'components/Icons/CloseV2.svg';

import { useAppContext } from 'hooks/context/useAppContext';
import { useNavigation } from 'hooks/navigation/useNavigation';
import { useGGTheme } from 'hooks/theme/useGGTheme';

import { BagCounter } from 'routes/shop/bag/components/BagCounter/BagCounter';

import GGLogo from '../../../../../public/assets/logos/GG_Logo_Black.svg';
import PoweredBy from '../../../../../public/assets/themes/canvas/img/poweredby.svg';
import { EnergyNavItem } from './EnergyNavItem';

import s from './EnergyNavigation.module.css';

const EnergyNavigation = () => {
  const { publicUser } = useAppContext();
  const { theme } = useGGTheme();
  const isEnergyPalette = theme && theme.palette.name === 'Energy';

  const {
    isMenuOpen,
    showTeam,
    showPortfolio,
    showShopLink,
    aboutUrl,
    teamUrl,
    servicesUrl,
    portfolioUrl,
    contactUrl,
    bookUrl,
    bagUrl,
    isPackagePurchaseEnabled,
    isFloating,
    floatingNavRef,
    // termsOfUseUrl,
    // privacyPolicyUrl,
    shopUrl,
    shopSubNavigationItems,
    membershipsUrl,
    giftCardsUrl,
    renderOverlay,
    renderBagButton,
    renderMenuButton,
    tracking,
    renderMenuContainer,
    renderCloseButton,
    navigationRef,
    packagesUrl,
  } = useNavigation({
    styles: {
      overlay: s.overlay,
      overlayOpen: s.overlayOpen,
      overlayClosed: s.overlayClosed,
      menuButton: s.button,
      bagButton: cx(s.bagButton, { [s.bagCounter]: isEnergyPalette }),
      menuContainer: s.menuContainer,
      menuContainerOpen: s.menuContainerOpen,
      menuContainerClosed: s.menuContainerClosed,
    },
  });

  return (
    <nav className={s.root} ref={navigationRef} aria-label="main navigation">
      <div className={s.topPlaceholder} ref={floatingNavRef} id="top-of-nav" />

      <div
        id="nav-bar"
        data-testid="nav-bar"
        className={cx(s.container, {
          [s.isFloating]: isFloating,
          [s.energyPalette]: isEnergyPalette,
        })}
      >
        <div className={s.title}>
          <Link href="/">{publicUser.business_name.toUpperCase()}</Link>
        </div>

        {renderOverlay()}
        <CanvasAnimatedTile className="navContainer">
          <div className={s.desktopFlexContainer}>
            <ul className={cx(s.desktopNav, s.menu)}>
              <EnergyNavItem to={servicesUrl}>Services</EnergyNavItem>
              <EnergyNavItem to={aboutUrl}>About</EnergyNavItem>
              {showTeam && (
                <EnergyNavItem to={teamUrl}>
                  <span data-testid="our-team">
                    <span className={s.fullText}>Team</span>
                    <span className={s.minimalText}>Team</span>
                  </span>
                </EnergyNavItem>
              )}
              {showPortfolio && (
                <EnergyNavItem to={portfolioUrl}>Portfolio</EnergyNavItem>
              )}
              {packagesUrl && !isPackagePurchaseEnabled && (
                <EnergyNavItem
                  onClick={() => tracking.navItemClicked('packages')}
                  to={packagesUrl}
                >
                  Packages
                </EnergyNavItem>
              )}
              {isPackagePurchaseEnabled && (
                <EnergyNavItem subNavItems={shopSubNavigationItems}>
                  Shop
                </EnergyNavItem>
              )}

              <EnergyNavItem to={contactUrl}>Contact</EnergyNavItem>
              {isPackagePurchaseEnabled && (
                <EnergyNavItem
                  to={bagUrl}
                  className={s.bagItem}
                  ariaLabel="Bag preview"
                >
                  <BagCounter size={28} className={s.bagCounter} />
                </EnergyNavItem>
              )}
              <EnergyNavItem className={s.bookNowCTA} to={bookUrl}>
                <div data-testid="book-now">
                  <span className={s.fullText}>Book Now</span>
                  <span className={s.minimalText}>Book</span>
                </div>
              </EnergyNavItem>
              {showShopLink && (
                <EnergyNavItem newTab to={shopUrl}>
                  <span className={s.fullText}>Shop Now</span>
                  <span className={s.minimalText}>Shop</span>
                </EnergyNavItem>
              )}
            </ul>
          </div>
        </CanvasAnimatedTile>
        <div className={s.combinedNavigation}>
          {renderBagButton(isMenuOpen ? s.hideOnOpenMobile : '')}
          {renderMenuButton({
            content: (
              <MenuIcon
                className={cx(s.menuIcon, isMenuOpen ? s.hideOnOpenMobile : '')}
              />
            ),
          })}
        </div>
        {renderMenuContainer({
          isOpen: isMenuOpen,
          content: (
            <>
              <div className={s.headerContent}>
                <Heading as="h2">
                  {publicUser?.business_name.toUpperCase()}
                </Heading>
                <div className={s.closeButtonContainer}>
                  {renderCloseButton({
                    content: <CloseIcon className={s.closeIcon} />,
                  })}
                </div>
              </div>
              <ul className={cx(s.mobileNav, s.menu)}>
                <EnergyNavItem
                  onClick={() => tracking.navItemClicked('book')}
                  to={bookUrl}
                >
                  <div data-testid="book-now" className={s.bookNowCTA}>
                    <span className={s.fullText}>Book Now</span>
                    <span className={s.minimalText}>Book Now</span>
                  </div>
                </EnergyNavItem>
                <EnergyNavItem
                  onClick={() => tracking.navItemClicked('about')}
                  className={s.aboutPage}
                  to={aboutUrl}
                >
                  About
                </EnergyNavItem>
                {showTeam && (
                  <EnergyNavItem
                    onClick={() => tracking.navItemClicked('team')}
                    className={s.teamPage}
                    to={teamUrl}
                  >
                    <span data-testid="our-team">
                      <span className={s.fullText}>Our Team</span>
                      <span className={s.minimalText}>Team</span>
                    </span>
                  </EnergyNavItem>
                )}
                <EnergyNavItem
                  onClick={() => tracking.navItemClicked('services')}
                  className={cx(
                    !showTeam ? s.servicesWithNoTeam : s.servicesPage,
                  )}
                  to={servicesUrl}
                >
                  Services
                </EnergyNavItem>

                {showPortfolio && (
                  <EnergyNavItem
                    onClick={() => tracking.navItemClicked('portfolio')}
                    className={s.portfolioPage}
                    to={portfolioUrl}
                  >
                    Portfolio
                  </EnergyNavItem>
                )}

                {packagesUrl && (
                  <EnergyNavItem
                    onClick={() => tracking.navItemClicked('packages')}
                    to={packagesUrl}
                  >
                    Packages
                  </EnergyNavItem>
                )}
                {membershipsUrl && (
                  <EnergyNavItem
                    onClick={() => tracking.navItemClicked('memberships')}
                    to={membershipsUrl}
                  >
                    Memberships
                  </EnergyNavItem>
                )}
                {giftCardsUrl && (
                  <EnergyNavItem
                    onClick={() => tracking.navItemClicked('gift-cards')}
                    to={giftCardsUrl}
                  >
                    Gift Cards
                  </EnergyNavItem>
                )}

                <EnergyNavItem
                  onClick={() => tracking.navItemClicked('contact')}
                  className={cx(
                    showPortfolio
                      ? s.contactsPagePortfolioPersist
                      : s.contactsPage,
                  )}
                  to={contactUrl}
                >
                  Contact
                </EnergyNavItem>
                {showShopLink && (
                  <EnergyNavItem
                    onClick={() => tracking.navItemClicked('shop')}
                    className={s.shopNowCTA}
                    newTab
                    to={shopUrl}
                  >
                    <span className={s.fullText}>Shop Now</span>
                    <span className={s.minimalText}>Shop</span>
                  </EnergyNavItem>
                )}
              </ul>
              <div className={cx(s.copyright)}>
                <div>
                  <a
                    href="https://glossgenius.com"
                    className={s.logo}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <PoweredBy
                      className={s.poweredBy}
                      width="125"
                      height="13"
                    />
                    <GGLogo className={s.ggLogoSVG} width="125" height="13" />
                  </a>
                </div>
              </div>
            </>
          ),
        })}
      </div>
    </nav>
  );
};

export { EnergyNavigation };
